<template>
  <v-dialog
    v-model="isDialerOpen"
    max-width="380"
    persistent
    overlay-opacity="0.4"
    transition="dialog-bottom-transition"
    content-class="dialer-dialog"
  >
    <v-card
      class="compact-dialer"
      :class="{ 'in-call': isInCall, 'call-failed': callStatus === 'failed' }"
    >
      <!-- Animated Background Gradient -->
      <div
        class="dialer-bg-gradient"
        :class="{ 'active-call': isInCall }"
      ></div>

      <!-- Header -->
      <v-card-title class="dialer-header py-3">
        <div class="header-icon-container">
          <v-icon class="header-icon" :class="{ 'icon-pulse': isInCall }"
            >mdi-phone</v-icon
          >
          <span v-if="callStatus === 'ringing'" class="header-ring"></span>
        </div>
        <span class="header-title">Dialer</span>
        <v-spacer></v-spacer>

        <!-- Connection Status Indicator -->
        <div class="status-chip" :class="`status-${connectionStatus}`">
          <span class="status-dot"></span>
          <span class="status-text">{{ connectionText }}</span>
        </div>

        <!-- Sound Toggle -->
        <v-btn
          icon
          small
          class="sound-toggle ml-2"
          :title="soundEnabled ? 'Mute sounds' : 'Enable sounds'"
          @click="toggleSound"
        >
          <v-icon small>{{
            soundEnabled ? "mdi-volume-high" : "mdi-volume-off"
          }}</v-icon>
        </v-btn>

        <!-- Close Button -->
        <v-btn icon small class="close-btn ml-1" @click="closeDialer">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <!-- Call Status Section -->
      <v-card-text class="dialer-body pb-2">
        <transition name="slide-fade" mode="out-in">
          <div
            v-if="isInCall || callStatus !== 'idle'"
            key="call-active"
            class="call-status-section text-center mb-4"
          >
            <!-- Animated Avatar -->
            <div
              class="caller-avatar"
              :class="{
                'avatar-pulse':
                  callStatus === 'ringing' || callStatus === 'calling',
              }"
            >
              <v-icon size="40" color="white">mdi-account</v-icon>
              <div class="avatar-ring"></div>
              <div class="avatar-ring delay-1"></div>
              <div class="avatar-ring delay-2"></div>
            </div>

            <!-- Contact Name (if available) -->
            <div v-if="currentContactName" class="call-name-display">
              <transition name="number-pop" mode="out-in">
                <span :key="currentContactName">{{ currentContactName }}</span>
              </transition>
            </div>

            <div
              class="call-number-display"
              :class="{ 'has-name': currentContactName }"
            >
              <transition name="number-pop" mode="out-in">
                <span :key="currentNumber">{{
                  currentNumber || "Unknown"
                }}</span>
              </transition>
            </div>

            <div class="call-status-badge" :class="`badge-${callStatus}`">
              <span class="status-icon-wrapper">
                <v-icon small class="status-icon">{{
                  getStatusIcon(callStatus)
                }}</v-icon>
              </span>
              {{ statusText }}
            </div>

            <transition name="fade-up">
              <div
                v-if="isInCall && callDuration > 0"
                class="call-duration-display"
              >
                <v-icon small class="mr-1">mdi-timer-outline</v-icon>
                {{ formatDuration(callDuration) }}
              </div>
            </transition>
          </div>
        </transition>

        <!-- Phone Number Input - Hidden during active call -->
        <div v-if="!isCallActive" class="phone-input-wrapper">
          <div
            ref="phoneInputContainer"
            class="phone-input-container"
            :class="{ 'input-focused': inputFocused }"
          >
            <div class="input-icon">
              <v-icon :color="inputFocused ? 'primary' : 'grey'"
                >mdi-phone-outline</v-icon
              >
            </div>
            <input
              ref="phoneInput"
              v-model="phoneNumber"
              type="tel"
              placeholder="Enter phone number"
              class="phone-input"
              :disabled="isInCall"
              @focus="handleInputFocus"
              @blur="handleInputBlur"
              @keyup.enter="makeCall"
              @paste="handlePaste"
              @input="sanitizePhoneInput"
            />
            <div class="input-actions">
              <transition name="fade">
                <v-progress-circular
                  v-if="isSearchingContacts"
                  indeterminate
                  size="18"
                  width="2"
                  color="primary"
                  class="mr-1"
                ></v-progress-circular>
              </transition>
              <transition name="fade">
                <v-btn
                  v-if="phoneNumber"
                  icon
                  x-small
                  class="clear-btn"
                  @click="clearNumber"
                >
                  <v-icon small>mdi-close-circle</v-icon>
                </v-btn>
              </transition>
              <v-btn
                icon
                small
                class="dialpad-toggle"
                :class="{ active: showDialPad }"
                @click="toggleDialPad"
              >
                <v-icon>mdi-dialpad</v-icon>
              </v-btn>
            </div>
          </div>

          <!-- Contact Search Results Dropdown -->
          <transition name="dropdown-slide">
            <div
              v-if="showContactResults && contactSearchResults.length > 0"
              class="contact-results-dropdown"
              :style="dropdownPositionStyle"
            >
              <div
                v-for="contact in contactSearchResults"
                :key="contact.id"
                class="contact-result-item"
                @mousedown.prevent="selectContact(contact)"
              >
                <div class="contact-avatar">
                  <v-icon small color="white">mdi-account</v-icon>
                </div>
                <div class="contact-info">
                  <div class="contact-name">
                    {{ contact.name || "Unknown" }}
                  </div>
                  <div class="contact-phone">{{ contact.phone }}</div>
                </div>
                <div class="contact-right">
                  <span v-if="contact.listName" class="contact-list-badge">
                    <v-icon x-small class="mr-1"
                      >mdi-format-list-bulleted</v-icon
                    >
                    {{ contact.listName }}
                  </span>
                  <div v-if="contact.secondPhone" class="contact-secondary">
                    <v-icon x-small class="mr-1">mdi-phone-plus</v-icon>
                    {{ contact.secondPhone }}
                  </div>
                </div>
              </div>
              <div
                v-if="contactSearchResults.length >= 10"
                class="contact-results-hint"
              >
                <v-icon x-small class="mr-1">mdi-information-outline</v-icon>
                Type more digits to narrow results
              </div>
            </div>
          </transition>
        </div>

        <!-- Dial Pad with Animations - Hidden during active call -->
        <v-expand-transition>
          <div v-show="showDialPad && !isCallActive" class="dial-pad">
            <div class="dial-pad-grid">
              <button
                v-for="(digit, index) in dialPadNumbers"
                :key="digit.number"
                class="dial-btn"
                :class="{ pressed: pressedDigit === digit.number }"
                :style="{ animationDelay: `${index * 30}ms` }"
                :disabled="isCallActive"
                @mousedown="handleDigitPress(digit.number)"
                @mouseup="handleDigitRelease"
                @mouseleave="handleDigitRelease"
                @touchstart.prevent="handleDigitPress(digit.number)"
                @touchend.prevent="handleDigitRelease"
              >
                <span class="btn-ripple"></span>
                <span class="digit-number">{{ digit.number }}</span>
                <span class="digit-letters">{{ digit.letters }}</span>
              </button>
            </div>

            <!-- Backspace Button -->
            <div class="dial-pad-actions">
              <v-btn
                icon
                class="backspace-btn"
                :disabled="!phoneNumber || isCallActive"
                @click="backspace"
              >
                <v-icon>mdi-backspace-outline</v-icon>
              </v-btn>
            </div>
          </div>
        </v-expand-transition>

        <!-- Call Controls -->
        <div class="call-controls">
          <transition name="scale" mode="out-in">
            <button
              v-if="!isCallActive"
              key="call-btn"
              class="call-action-btn call-btn"
              :class="{ disabled: !canMakeCall || !phoneNumber.trim() }"
              :disabled="!canMakeCall || !phoneNumber.trim()"
              @click="makeCall"
            >
              <span class="btn-bg"></span>
              <v-icon class="btn-icon">mdi-phone</v-icon>
              <span class="btn-text">Call</span>
            </button>

            <button
              v-else
              key="end-btn"
              class="call-action-btn end-btn"
              @click="endCall"
            >
              <span class="btn-bg"></span>
              <v-icon class="btn-icon">mdi-phone-hangup</v-icon>
              <span class="btn-text">End Call</span>
            </button>
          </transition>

          <transition name="fade">
            <button
              v-if="!isConnected && !isConnecting"
              class="connect-btn"
              @click="reconnect"
            >
              <v-icon small class="mr-2">mdi-connection</v-icon>
              Connect
            </button>
          </transition>

          <transition name="fade">
            <div v-if="isConnecting" class="connecting-indicator">
              <v-progress-circular
                indeterminate
                size="18"
                width="2"
                color="primary"
              ></v-progress-circular>
              <span>Connecting...</span>
            </div>
          </transition>
        </div>
      </v-card-text>

      <!-- Error Message -->
      <transition name="slide-up">
        <div v-if="callStatus === 'failed'" class="error-banner">
          <v-icon small color="white" class="mr-2">mdi-alert-circle</v-icon>
          <span>{{ callErrorMessage }}</span>
          <v-btn icon x-small dark class="ml-2" @click="clearError">
            <v-icon small>mdi-close</v-icon>
          </v-btn>
        </div>
      </transition>
    </v-card>

    <!-- Hidden Audio Element for Remote Audio with mobile-specific attributes -->
    <audio
      id="compact-dialer-audio"
      ref="remoteAudio"
      autoplay
      playsinline
      webkit-playsinline
      x-webkit-airplay="allow"
      preload="auto"
      style="display: none;"
    ></audio>

    <!-- Save New Contact Dialog -->
    <v-dialog v-model="showSaveContactDialog" max-width="400" persistent>
      <v-card class="customBorderRadius">
        <v-card-title class="primarygrad" style="border-radius: 12px 12px 0 0;">
          <v-icon left color="white">mdi-account-plus</v-icon>
          <span class="white--text">Save New Contact</span>
        </v-card-title>
        <v-card-text class="pt-6">
          <p class="mb-4 grey--text text--darken-1">
            This number is not saved. Would you like to save it as a contact?
          </p>
          <v-text-field
            v-model="newContactPhone"
            label="Phone Number"
            prepend-inner-icon="mdi-phone"
            outlined
            dense
            readonly
            class="customBorderRadius mb-2"
          />
          <v-text-field
            v-model="newContactName"
            label="Contact Name"
            prepend-inner-icon="mdi-account"
            outlined
            dense
            autofocus
            class="customBorderRadius"
            placeholder="Enter contact name"
            @keyup.enter="saveNewContactAndCall"
          />
          <v-select
            v-model="newContactListId"
            :items="availableLists"
            item-text="name"
            item-value="id"
            label="Select List"
            prepend-inner-icon="mdi-format-list-bulleted"
            outlined
            dense
            class="customBorderRadius"
          />
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions class="pa-4">
          <v-btn
            text
            color="grey"
            class="customBorderRadius"
            @click="skipSaveAndCall"
          >
            Skip & Call
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            text
            color="red"
            class="customBorderRadius"
            @click="cancelSaveContact"
          >
            Cancel
          </v-btn>
          <v-btn
            depressed
            color="primarygrad"
            class="customBorderRadius"
            :loading="isSavingContact"
            :disabled="!newContactName.trim() || !newContactListId"
            @click="saveNewContactAndCall"
          >
            Save & Call
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-dialog>
</template>

<script>
import { mapState, mapGetters } from "vuex";
import dialerSounds from "../js/dialerSounds";
import _ from "lodash";

export default {
  name: "CompactDialer",
  components: {},
  data() {
    return {
      phoneNumber: "",
      isConnecting: false,
      inputFocused: false,
      pressedDigit: null,
      soundEnabled: true,
      previousCallStatus: "idle",
      // Contact search data
      contactSearchResults: [],
      isSearchingContacts: false,
      searchTimeout: null,
      cachedListIds: null,
      cachedListsMap: null, // Map of listId -> listName
      showContactResults: false,
      selectedContactId: null, // Track selected contact's ID for disposition
      selectedContactListId: null, // Track selected contact's listId for disposition
      dropdownPosition: { top: 0, left: 0, width: 0 }, // Track dropdown position
      dialPadNumbers: [
        { number: "1", letters: "" },
        { number: "2", letters: "ABC" },
        { number: "3", letters: "DEF" },
        { number: "4", letters: "GHI" },
        { number: "5", letters: "JKL" },
        { number: "6", letters: "MNO" },
        { number: "7", letters: "PQRS" },
        { number: "8", letters: "TUV" },
        { number: "9", letters: "WXYZ" },
        { number: "*", letters: "" },
        { number: "0", letters: "+" },
        { number: "#", letters: "" },
      ],
      // Save contact dialog
      showSaveContactDialog: false,
      newContactName: "",
      newContactPhone: "",
      newContactListId: null,
      isSavingContact: false,
      availableLists: [],
    };
  },
  computed: {
    ...mapState("dialer", [
      "connectionStatus",
      "callStatus",
      "callError",
      "isInCall",
      "callDuration",
      "currentNumber",
      "currentContactName",
      "isDialerOpen",
      "showDialPad",
      "prefillNumber",
      "prefillContactName",
    ]),
    ...mapGetters("dialer", [
      "isConnected",
      "canMakeCall",
      "connectionText",
      "statusText",
    ]),
    // User-friendly error message for the error banner
    callErrorMessage() {
      return this.callError || "Call failed. Please try again.";
    },
    // Check if there's an active call attempt (calling, ringing, or connected)
    isCallActive() {
      return (
        this.callStatus === "calling" ||
        this.callStatus === "ringing" ||
        this.callStatus === "connected" ||
        this.isInCall
      );
    },
    // Computed style for fixed-position dropdown
    dropdownPositionStyle() {
      return {
        position: "fixed",
        top: `${this.dropdownPosition.top}px`,
        left: `${this.dropdownPosition.left}px`,
        width: `${this.dropdownPosition.width}px`,
      };
    },
  },
  watch: {
    // Watch for prefill number from contact popup
    prefillNumber(newNumber) {
      if (newNumber) {
        this.phoneNumber = newNumber;
        // Set contact name if provided
        if (this.prefillContactName) {
          this.$store.commit(
            "dialer/SET_CURRENT_CONTACT_NAME",
            this.prefillContactName
          );
        }
        // Clear the prefill after using it
        this.$store.commit("dialer/CLEAR_PREFILL");
      }
    },
    // Watch phoneNumber for contact search and clear name if manually changed
    phoneNumber(newValue, oldValue) {
      this.debouncedSearchContacts(newValue);
      this.updateDropdownPosition(); // Update position when typing

      // Clear contact name if user is manually editing the number
      // (contact name is only valid when number exactly matches selected contact)
      // Don't clear during auto-dialing as the name is set by the auto-dialer
      const isAutoDialing = this.$store.state.dialer.isAutoDialing;
      if (this.currentContactName && newValue !== oldValue && !isAutoDialing) {
        // Check if this is a manual edit vs contact selection
        // Contact selection sets the name, so if name exists but number changed, clear it
        const storedNumber = this.$store.state.dialer.currentNumber;
        if (!storedNumber || newValue !== storedNumber) {
          this.$store.commit("dialer/SET_CURRENT_CONTACT_NAME", "");
        }
      }
    },
    // Play sounds on call status changes
    callStatus(newStatus, oldStatus) {
      if (!this.soundEnabled) return;

      if (newStatus === "connected" && oldStatus !== "connected") {
        dialerSounds.playConnected();
      } else if (newStatus === "failed") {
        dialerSounds.playError();
        // Auto-dismiss failed state after 5 seconds
        setTimeout(() => {
          if (this.$store.state.dialer.callStatus === "failed") {
            this.$store.commit("dialer/RESET_CALL_STATE");
          }
        }, 5000);
      } else if (newStatus === "idle" && oldStatus === "connected") {
        dialerSounds.playHangup();
      }
    },
    // Initialize sounds when dialer opens
    isDialerOpen(isOpen) {
      if (isOpen) {
        dialerSounds.init();
        // Reset failed state when opening dialer
        if (this.$store.state.dialer.callStatus === "failed") {
          this.$store.commit("dialer/RESET_CALL_STATE");
        }
      }
    },
  },
  async mounted() {
    // Initialize sound system
    dialerSounds.init();

    // Load sound preference from localStorage
    const savedSoundPref = localStorage.getItem("dialer-sound-enabled");
    if (savedSoundPref !== null) {
      this.soundEnabled = savedSoundPref === "true";
    }

    // Load settings and auto-connect if available
    await this.$store.dispatch("dialer/loadSettings");

    // Auto-connect is now handled by initializeDialer in main.js
    // Only reconnect if we're disconnected and have credentials
    const { userId, authToken } = this.$store.state.dialer.settings;
    const connectionStatus = this.$store.state.dialer.connectionStatus;
    if (userId && authToken && connectionStatus === "disconnected") {
      try {
        await this.reconnect();
      } catch (error) {
        console.log("Auto-connect failed:", error.message);
      }
    }
  },
  methods: {
    // Debounced contact search
    debouncedSearchContacts: _.debounce(function(searchValue) {
      this.searchContacts(searchValue);
    }, 300),

    async searchContacts(searchValue) {
      // Clear results if search is too short
      if (!searchValue || searchValue.length < 2) {
        this.contactSearchResults = [];
        this.isSearchingContacts = false;
        return;
      }

      // Only search for numeric input
      if (!/^\d+$/.test(searchValue.replace(/[\s\-\+\(\)]/g, ""))) {
        this.contactSearchResults = [];
        this.isSearchingContacts = false;
        return;
      }

      this.isSearchingContacts = true;

      try {
        // Get list IDs from cached data or fetch them
        const listIds = await this.getListIds();

        if (!listIds || listIds.length === 0) {
          this.contactSearchResults = [];
          this.isSearchingContacts = false;
          return;
        }

        const response = await this.$http.post(
          `${this.$store.state.ENDPOINT}/contacts/query-contact-multilist-v2`,
          {
            search: searchValue,
            listIds: listIds,
          }
        );

        if (response && response.body) {
          // Limit to 10 results and add list names
          const contacts = response.body.slice(0, 10).map((contact) => {
            return {
              ...contact,
              listName:
                this.cachedListsMap && contact.listId
                  ? this.cachedListsMap[contact.listId]
                  : null,
            };
          });
          this.contactSearchResults = contacts;
        } else {
          this.contactSearchResults = [];
        }
      } catch (error) {
        console.error("Error searching contacts:", error);
        this.contactSearchResults = [];
      } finally {
        this.isSearchingContacts = false;
      }
    },

    async getListIds() {
      // Return cached list IDs if available
      if (this.cachedListIds && this.cachedListIds.length > 0) {
        return this.cachedListIds;
      }

      try {
        // Get teams from store
        const teams = this.$store.state.teams;
        if (!teams || teams.length === 0) {
          return [];
        }

        // Get team IDs (exclude 'allTeams')
        const teamIds = teams
          .filter((t) => t.id !== "allTeams")
          .map((t) => t.id);

        if (teamIds.length === 0) {
          return [];
        }

        // Fetch lists for these teams
        const response = await this.$http.post(
          `${this.$store.state.ENDPOINT}/lists/fetch`,
          { teams: teamIds }
        );

        if (response && response.body && response.body.length > 0) {
          // Cache the list IDs
          this.cachedListIds = response.body.map((list) => list.id);

          // Cache list names map (listId -> listName)
          this.cachedListsMap = {};
          response.body.forEach((list) => {
            this.cachedListsMap[list.id] = list.name;
          });

          return this.cachedListIds;
        }

        return [];
      } catch (error) {
        console.error("Error fetching list IDs:", error);
        return [];
      }
    },

    selectContact(contact) {
      // Set the phone number from selected contact
      this.phoneNumber = contact.phone;

      // Store contact ID and listId so disposition dialog can find the existing contact
      this.selectedContactId = contact.id || null;
      this.selectedContactListId = contact.listId || null;

      // Store contact name in Vuex for display in call UI and disposition
      this.$store.commit("dialer/SET_CURRENT_CONTACT_NAME", contact.name || "");

      this.contactSearchResults = [];
      this.showContactResults = false;

      if (this.soundEnabled) {
        dialerSounds.playClick();
      }

      // Focus back on the input
      this.$nextTick(() => {
        if (this.$refs.phoneInput) {
          this.$refs.phoneInput.focus();
        }
      });
    },

    handleInputFocus() {
      this.inputFocused = true;
      this.showContactResults = true;
      this.updateDropdownPosition();
    },

    handleInputBlur() {
      this.inputFocused = false;
      // Delay hiding to allow click on results
      setTimeout(() => {
        this.showContactResults = false;
      }, 200);
    },

    handlePaste(event) {
      // Prevent default paste behavior
      event.preventDefault();

      // Get pasted text from clipboard
      const pastedText = (event.clipboardData || window.clipboardData).getData(
        "text"
      );

      // Filter to only allow numbers and + sign
      const sanitized = pastedText.replace(/[^0-9+]/g, "");

      // Insert sanitized text at cursor position
      const input = event.target;
      const start = input.selectionStart;
      const end = input.selectionEnd;

      this.phoneNumber =
        this.phoneNumber.substring(0, start) +
        sanitized +
        this.phoneNumber.substring(end);

      // Set cursor position after pasted content
      this.$nextTick(() => {
        input.selectionStart = input.selectionEnd = start + sanitized.length;
      });
    },

    sanitizePhoneInput() {
      // Remove any characters that are not numbers or + sign
      this.phoneNumber = this.phoneNumber.replace(/[^0-9+]/g, "");
      // Clear selected contact when user manually edits the number
      this.selectedContactId = null;
      this.selectedContactListId = null;
    },

    getStatusIcon(status) {
      const icons = {
        idle: "mdi-phone-outline",
        calling: "mdi-phone-outgoing",
        ringing: "mdi-phone-ring",
        connected: "mdi-phone-in-talk",
        failed: "mdi-phone-missed",
      };
      return icons[status] || "mdi-phone";
    },

    toggleSound() {
      this.soundEnabled = !this.soundEnabled;
      localStorage.setItem(
        "dialer-sound-enabled",
        this.soundEnabled.toString()
      );
      dialerSounds.setEnabled(this.soundEnabled);

      // Play a click to confirm sound is on
      if (this.soundEnabled) {
        dialerSounds.playClick();
      }
    },

    handleDigitPress(digit) {
      console.log("Digit pressed:", digit);

      if (this.isInCall) return;

      this.pressedDigit = digit;
      this.phoneNumber += digit;

      // Play DTMF tone
      if (this.soundEnabled) {
        dialerSounds.playDTMF(digit, 120);
      }

      // Also send DTMF if in call (for IVR navigation)
      if (this.isInCall) {
        this.$store.dispatch("dialer/sendDTMF", digit);
      }
    },

    handleDigitRelease() {
      this.pressedDigit = null;
    },

    addDigit(digit) {
      this.handleDigitPress(digit);
      setTimeout(() => this.handleDigitRelease(), 100);
    },

    backspace() {
      if (this.phoneNumber.length > 0) {
        this.phoneNumber = this.phoneNumber.slice(0, -1);
        if (this.soundEnabled) {
          dialerSounds.playClick();
        }
      }
    },

    clearNumber() {
      this.phoneNumber = "";
      if (this.soundEnabled) {
        dialerSounds.playClick();
      }
    },

    async reconnect() {
      this.isConnecting = true;
      try {
        await this.$store.dispatch("dialer/connect");
        if (this.soundEnabled) {
          dialerSounds.playConnected();
        }
      } catch (error) {
        if (this.soundEnabled) {
          dialerSounds.playError();
        }
        this.$swal({
          title: "Connection Failed",
          text: error.message,
          type: "error",
          confirmButtonText: "OK",
        });
      } finally {
        this.isConnecting = false;
      }
    },

    async makeCall() {
      if (!this.phoneNumber.trim()) return;

      if (this.soundEnabled) {
        dialerSounds.playClick();
      }

      // Check PBX trial call limit
      try {
        const companyDetails = this.$store.getters.companyDetails;
        if (
          companyDetails &&
          companyDetails.plan &&
          companyDetails.plan.name === "Trial"
        ) {
          const response = await this.$http.get(
            `${this.$store.state.ENDPOINT}/eventMappingCaches/getPbxTrialCallCount?companyId=${companyDetails.id}`
          );
          const data = response.body;
          if (data.isPbxCallLimitReached) {
            this.$swal({
              title: "PBX Trial Limit Reached",
              text:
                data.message ||
                "Your PBX trial call limit of 100 calls has been reached. Please subscribe to continue using PBX calling.",
              type: "warning",
              confirmButtonText: "Subscribe Now",
              showCancelButton: true,
              cancelButtonText: "Cancel",
            }).then((result) => {
              if (result.value) {
                this.$router.push("/subscribe");
              }
            });
            return;
          }
        }
      } catch (limitErr) {
        console.error("Error checking PBX trial limit:", limitErr);
      }

      // Check if contact is saved
      try {
        const phoneToCheck = this.phoneNumber.trim();
        const companyDetails = this.$store.getters.companyDetails;
        const companyId = companyDetails && companyDetails.id;

        if (companyId) {
          const searchResponse = await this.$http.get(
            `${this.$store.state.ENDPOINT}/contacts`,
            {
              params: {
                filter: JSON.stringify({
                  where: {
                    phone: phoneToCheck,
                    companyId: companyId,
                  },
                  limit: 1,
                }),
              },
            }
          );

          const existingContacts =
            searchResponse.body || searchResponse.data || [];

          // If contact not found, show save contact dialog
          if (!existingContacts || existingContacts.length === 0) {
            await this.fetchAvailableLists();
            this.newContactPhone = phoneToCheck;
            this.newContactName = "";
            this.newContactListId =
              this.availableLists.length > 0 ? this.availableLists[0].id : null;
            this.showSaveContactDialog = true;
            return;
          } else {
            // Contact exists, set contact name and ID for display and disposition
            const existingContact = existingContacts[0];
            if (
              existingContact.name &&
              existingContact.name !== existingContact.phone
            ) {
              this.$store.commit(
                "dialer/SET_CURRENT_CONTACT_NAME",
                existingContact.name
              );
            }
            // Also capture contactId/listId if not already set from selectContact
            if (!this.selectedContactId && existingContact.id) {
              this.selectedContactId = existingContact.id;
              this.selectedContactListId = existingContact.listId || null;
            }
          }
        }
      } catch (checkErr) {
        console.error("Error checking if contact exists:", checkErr);
        // Continue with call even if check fails
      }

      // Proceed with call
      await this.proceedWithCall();
    },

    async proceedWithCall() {
      try {
        await this.$store.dispatch("dialer/makeCall", {
          targetNumber: this.phoneNumber.trim(),
          audioElement: this.$refs.remoteAudio,
          contactId: this.selectedContactId || null,
          listId: this.selectedContactListId || null,
        });
        // Clear selected contact after call is initiated
        this.selectedContactId = null;
        this.selectedContactListId = null;
      } catch (error) {
        this.$swal({
          title: "Call Failed",
          text: error.message,
          type: "error",
          confirmButtonText: "OK",
        });
      }
    },

    async fetchAvailableLists() {
      try {
        const companyDetails = this.$store.getters.companyDetails;
        const companyId = companyDetails && companyDetails.id;
        if (!companyId) return;

        const response = await this.$http.get(
          `${this.$store.state.ENDPOINT}/lists`,
          {
            params: {
              filter: JSON.stringify({
                where: { companyId: companyId },
                fields: { id: true, name: true },
              }),
            },
          }
        );
        this.availableLists = response.body || response.data || [];
      } catch (err) {
        console.error("Error fetching lists:", err);
        this.availableLists = [];
      }
    },

    async saveNewContactAndCall() {
      if (!this.newContactName.trim() || !this.newContactListId) return;

      this.isSavingContact = true;
      try {
        const companyDetails = this.$store.getters.companyDetails;
        const companyId = companyDetails && companyDetails.id;
        const user = this.$store.state.user;
        const userId = user && user.id;

        const newContact = {
          phone: this.newContactPhone,
          name: this.newContactName.trim(),
          companyId: companyId,
          accountsId: userId,
          listId: this.newContactListId,
          dispo: "NEW",
          called: false,
          customFields: {},
        };

        const response = await this.$http.post(
          `${this.$store.state.ENDPOINT}/contacts`,
          newContact
        );

        console.log("✅ Contact saved:", response.body || response.data);

        // Set contact name for display during call
        this.$store.commit(
          "dialer/SET_CURRENT_CONTACT_NAME",
          this.newContactName.trim()
        );

        // Close dialog and proceed with call
        this.showSaveContactDialog = false;
        this.resetSaveContactForm();

        this.$swal({
          icon: "success",
          title: "Contact saved successfully!",
          toast: true,
          position: "top-end",
          showConfirmButton: false,
          timer: 3000,
        });

        await this.proceedWithCall();
      } catch (err) {
        console.error("Error saving contact:", err);
        this.$swal({
          icon: "error",
          title: "Failed to save contact",
          text: err.message || "Unknown error",
          toast: true,
          position: "top-end",
          showConfirmButton: false,
          timer: 3000,
        });
      } finally {
        this.isSavingContact = false;
      }
    },

    skipSaveAndCall() {
      this.showSaveContactDialog = false;
      this.resetSaveContactForm();
      this.proceedWithCall();
    },

    cancelSaveContact() {
      this.showSaveContactDialog = false;
      this.resetSaveContactForm();
    },

    resetSaveContactForm() {
      this.newContactName = "";
      this.newContactPhone = "";
      this.newContactListId = null;
    },

    endCall() {
      if (this.soundEnabled) {
        dialerSounds.playHangup();
      }
      this.$store.dispatch("dialer/endCall");
    },

    closeDialer() {
      if (this.soundEnabled) {
        dialerSounds.playClick();
      }
      this.$store.dispatch("dialer/closeDialer");
    },

    toggleDialPad() {
      if (this.soundEnabled) {
        dialerSounds.playClick();
      }
      this.$store.dispatch("dialer/toggleDialPad");
    },

    formatDuration(seconds) {
      const mins = Math.floor(seconds / 60);
      const secs = seconds % 60;
      return `${mins.toString().padStart(2, "0")}:${secs
        .toString()
        .padStart(2, "0")}`;
    },

    clearError() {
      // Reset the failed call state
      this.$store.commit("dialer/RESET_CALL_STATE");
      if (this.soundEnabled) {
        dialerSounds.playClick();
      }
    },

    updateDropdownPosition() {
      this.$nextTick(() => {
        if (this.$refs.phoneInputContainer) {
          const rect = this.$refs.phoneInputContainer.getBoundingClientRect();
          this.dropdownPosition = {
            top: rect.bottom,
            left: rect.left,
            width: rect.width,
          };
        }
      });
    },
  },
};
</script>

<style scoped>
/* ===== Base Styles ===== */
.compact-dialer {
  border-radius: 40px !important;
  overflow: visible !important; /* Allow dropdown to overflow */
  position: relative;
  background: #ffffff;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25) !important;
}

.compact-dialer.in-call {
  box-shadow: 0 25px 50px -12px rgba(76, 175, 80, 0.3) !important;
}

.compact-dialer.call-failed {
  animation: shake 0.5s ease-in-out;
}

/* Animated Background */
.dialer-bg-gradient {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 120px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  transition: all 0.5s ease;
  z-index: 0;
}

.dialer-bg-gradient.active-call {
  background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
  animation: gradientShift 3s ease infinite alternate;
}

/* ===== Header ===== */
.dialer-header {
  position: relative;
  z-index: 1;
  background: transparent !important;
  color: white;
  font-size: 16px;
  padding: 16px 20px !important;
  margin-bottom: 16px;
}

.header-icon-container {
  position: relative;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
}

.header-icon {
  color: white !important;
  font-size: 24px;
  transition: transform 0.3s ease;
}

.header-icon.icon-pulse {
  animation: iconPulse 1s ease-in-out infinite;
}

.header-ring {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 40px;
  height: 40px;
  border: 2px solid rgba(255, 255, 255, 0.5);
  border-radius: 50%;
  animation: ringExpand 1.5s ease-out infinite;
}

.header-title {
  font-weight: 600;
  font-size: 18px;
  letter-spacing: 0.5px;
}

/* Status Chip */
.status-chip {
  display: flex;
  align-items: center;
  padding: 6px 12px;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  font-size: 12px;
  font-weight: 500;
  transition: all 0.3s ease;
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 8px;
  transition: all 0.3s ease;
}

.status-disconnected .status-dot {
  background: #9e9e9e;
}
.status-connecting .status-dot {
  background: #ff9800;
  animation: blink 1s ease-in-out infinite;
}
.status-connected .status-dot {
  background: #2196f3;
}
.status-registered .status-dot {
  background: #4caf50;
  box-shadow: 0 0 8px #4caf50;
}
.status-error .status-dot {
  background: #f44336;
  animation: blink 0.5s ease-in-out infinite;
}

.sound-toggle,
.close-btn {
  color: rgba(255, 255, 255, 0.8) !important;
  transition: all 0.2s ease;
}

.sound-toggle:hover,
.close-btn:hover {
  color: white !important;
  background: rgba(255, 255, 255, 0.1);
}

/* ===== Body ===== */
.dialer-body {
  position: relative;
  z-index: 1;
  padding: 24px 20px !important;
  background: white;
  border-radius: 32px 32px 0 0;
  margin-top: -20px;
  overflow: visible !important; /* Allow dropdown to extend beyond */
}

/* Call Status Section */
.call-status-section {
  padding: 20px;
  background: linear-gradient(135deg, #f5f7fa 0%, #e4e8eb 100%);
  border-radius: 16px;
  margin-bottom: 20px;
}

.caller-avatar {
  position: relative;
  width: 80px;
  height: 80px;
  margin: 0 auto 16px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.caller-avatar.avatar-pulse .avatar-ring {
  animation: avatarRing 2s ease-out infinite;
}

.avatar-ring {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border: 2px solid #667eea;
  border-radius: 50%;
  opacity: 0;
}

.avatar-ring.delay-1 {
  animation-delay: 0.4s;
}
.avatar-ring.delay-2 {
  animation-delay: 0.8s;
}

.call-name-display {
  font-size: 20px;
  font-weight: 700;
  color: #1a1a2e;
  margin-bottom: 4px;
  letter-spacing: 0.5px;
}

.call-number-display {
  font-size: 24px;
  font-weight: 700;
  color: #1a1a2e;
  margin-bottom: 8px;
  letter-spacing: 1px;
}

.call-number-display.has-name {
  font-size: 16px;
  font-weight: 500;
  color: #666;
  letter-spacing: 0.5px;
}

.call-status-badge {
  display: inline-flex;
  align-items: center;
  padding: 6px 16px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 600;
  margin-bottom: 12px;
  transition: all 0.3s ease;
}

.status-icon-wrapper {
  display: flex;
  align-items: center;
  margin-right: 8px;
}

.badge-idle {
  background: #e0e0e0;
  color: #757575;
}
.badge-calling {
  background: linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%);
  color: #e65100;
}
.badge-ringing {
  background: linear-gradient(135deg, #a1c4fd 0%, #c2e9fb 100%);
  color: #1565c0;
  animation: badgePulse 1s ease-in-out infinite;
}
.badge-connected {
  background: linear-gradient(135deg, #d4fc79 0%, #96e6a1 100%);
  color: #2e7d32;
}
.badge-failed {
  background: linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%);
  color: #c62828;
}

.call-duration-display {
  display: inline-flex;
  align-items: center;
  font-size: 28px;
  font-weight: 700;
  color: #2e7d32;
  font-family: "SF Mono", "Roboto Mono", monospace;
  letter-spacing: 2px;
}

/* ===== Phone Input ===== */
.phone-input-container {
  display: flex;
  align-items: center;
  background: #f5f7fa;
  border: 2px solid transparent;
  border-radius: 16px;
  padding: 4px 8px 4px 16px;
  margin-bottom: 16px;
  transition: all 0.3s ease;
}

.phone-input-container.input-focused {
  background: white;
  border-color: #667eea;
  box-shadow: 0 0 0 4px rgba(102, 126, 234, 0.1);
}

.input-icon {
  margin-right: 12px;
  transition: all 0.3s ease;
}

.phone-input {
  flex: 1;
  border: none;
  background: transparent;
  font-size: 18px;
  font-weight: 500;
  color: #1a1a2e;
  padding: 12px 0;
  outline: none;
  letter-spacing: 1px;
}

.phone-input::placeholder {
  color: #9e9e9e;
  font-weight: 400;
}

.input-actions {
  display: flex;
  align-items: center;
  gap: 4px;
}

.clear-btn {
  opacity: 0.5;
  transition: all 0.2s ease;
}

.clear-btn:hover {
  opacity: 1;
}

.dialpad-toggle {
  transition: all 0.3s ease;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
}

.dialpad-toggle.active {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
  color: white !important;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
}

.dialpad-toggle.active .v-icon {
  color: white !important;
}

.dialpad-toggle:hover:not(.active) {
  background: rgba(102, 126, 234, 0.1) !important;
}

/* ===== Phone Input Wrapper ===== */
.phone-input-wrapper {
  position: relative;
  margin-bottom: 16px;
  z-index: 10000; /* Ensure wrapper has high z-index */
  overflow: visible !important; /* Don't clip dropdown */
}

.phone-input-wrapper .phone-input-container {
  margin-bottom: 0;
  position: relative;
  z-index: 10001;
}

/* ===== Contact Search Results ===== */
.contact-results-dropdown {
  /* Position will be set via inline style (fixed positioning) */
  position: fixed !important;
  max-height: 150px !important;
  overflow-y: auto !important;
  overflow-x: hidden !important;
  background: white;
  border: 1px solid #e0e0e0;
  border-top: none;
  border-radius: 0 0 16px 16px;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.12);
  z-index: 20000 !important; /* Much higher than v-dialog (200-300) */
  /* Smooth scrolling */
  scroll-behavior: smooth;
  /* Custom scrollbar */
  scrollbar-width: thin;
  scrollbar-color: #667eea #f5f5f5;
  /* Enable touch scrolling on mobile */
  -webkit-overflow-scrolling: touch;
  /* Force scroll container */
  display: block !important;
}

/* Webkit scrollbar styling - always visible */
.contact-results-dropdown::-webkit-scrollbar {
  width: 10px;
  background: #f5f5f5;
}

.contact-results-dropdown::-webkit-scrollbar-track {
  background: #f5f5f5;
  border-radius: 0 0 16px 0;
  margin: 4px 0;
}

.contact-results-dropdown::-webkit-scrollbar-thumb {
  background: #667eea;
  border-radius: 5px;
  border: 2px solid #f5f5f5;
  min-height: 40px;
}

.contact-results-dropdown::-webkit-scrollbar-thumb:hover {
  background: #764ba2;
}

.contact-results-dropdown::-webkit-scrollbar-thumb:active {
  background: #5568d3;
}

.contact-result-item {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  cursor: pointer;
  transition: all 0.15s ease;
  border-bottom: 1px solid #f5f5f5;
}

.contact-result-item:last-child {
  border-bottom: none;
}

.contact-result-item:hover {
  background: linear-gradient(
    135deg,
    rgba(102, 126, 234, 0.08) 0%,
    rgba(118, 75, 162, 0.08) 100%
  );
}

.contact-avatar {
  width: 36px;
  height: 36px;
  min-width: 36px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
}

.contact-info {
  flex: 1;
  min-width: 0;
}

.contact-name {
  font-size: 14px;
  font-weight: 600;
  color: #1a1a2e;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.contact-right {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
  margin-left: 8px;
  flex-shrink: 0;
}

.contact-list-badge {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  background: linear-gradient(
    135deg,
    rgba(102, 126, 234, 0.15) 0%,
    rgba(118, 75, 162, 0.15) 100%
  );
  border-radius: 10px;
  font-size: 10px;
  font-weight: 500;
  color: #667eea;
  white-space: nowrap;
  max-width: 100px;
  overflow: hidden;
  text-overflow: ellipsis;
}

.contact-list-badge .v-icon {
  color: #667eea !important;
  opacity: 0.8;
}

.contact-secondary {
  display: flex;
  align-items: center;
  font-size: 11px;
  color: #9e9e9e;
  white-space: nowrap;
}

.contact-phone {
  font-size: 13px;
  color: #667eea;
  font-weight: 500;
  letter-spacing: 0.5px;
}

.contact-results-hint {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px 16px;
  font-size: 12px;
  color: #9e9e9e;
  background: #f8f9fc;
  border-radius: 0 0 16px 16px;
}

/* Dropdown slide transition */
.dropdown-slide-enter-active,
.dropdown-slide-leave-active {
  transition: all 0.2s ease;
}

.dropdown-slide-enter,
.dropdown-slide-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

/* ===== Dial Pad ===== */
.dial-pad {
  background: #f8f9fc;
  border-radius: 20px;
  padding: 16px;
  margin-bottom: 16px;
}

.dial-pad-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
}

.dial-btn {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 64px;
  border: none;
  border-radius: 16px;
  background: white;
  cursor: pointer;
  transition: all 0.15s ease;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  animation: dialBtnAppear 0.3s ease backwards;
}

.dial-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.dial-btn:active:not(:disabled),
.dial-btn.pressed {
  transform: scale(0.95);
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.dial-btn.pressed .digit-number,
.dial-btn.pressed .digit-letters {
  color: white;
}

.dial-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-ripple {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 0;
  height: 0;
  background: rgba(102, 126, 234, 0.3);
  border-radius: 50%;
  transform: translate(-50%, -50%);
  transition: width 0.3s, height 0.3s;
}

.dial-btn.pressed .btn-ripple {
  width: 120px;
  height: 120px;
}

.digit-number {
  font-size: 24px;
  font-weight: 700;
  color: #1a1a2e;
  line-height: 1;
  transition: color 0.15s ease;
}

.digit-letters {
  font-size: 10px;
  font-weight: 600;
  color: #9e9e9e;
  letter-spacing: 2px;
  margin-top: 2px;
  transition: color 0.15s ease;
}

.dial-pad-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 10px;
}

.backspace-btn {
  background: transparent;
  color: #757575;
  transition: all 0.2s ease;
}

.backspace-btn:hover:not(:disabled) {
  color: #f44336;
  background: rgba(244, 67, 54, 0.1);
}

/* ===== Call Controls ===== */
.call-controls {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.call-action-btn {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 56px;
  border: none;
  border-radius: 16px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  overflow: hidden;
  transition: all 0.3s ease;
}

.call-btn {
  background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
  color: white;
  box-shadow: 0 4px 15px rgba(17, 153, 142, 0.4);
}

.call-btn:hover:not(.disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(17, 153, 142, 0.5);
}

.call-btn:active:not(.disabled) {
  transform: translateY(0);
}

.call-btn.disabled {
  background: #e0e0e0;
  color: #9e9e9e;
  box-shadow: none;
  cursor: not-allowed;
}

.end-btn {
  background: linear-gradient(135deg, #eb3349 0%, #f45c43 100%);
  color: white;
  box-shadow: 0 4px 15px rgba(235, 51, 73, 0.4);
  animation: endBtnPulse 2s ease-in-out infinite;
}

.end-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(235, 51, 73, 0.5);
}

.btn-bg {
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.2),
    transparent
  );
  transition: left 0.5s ease;
}

.call-action-btn:hover .btn-bg {
  left: 100%;
}

.btn-icon {
  font-size: 20px;
  margin-right: 8px;
}

.connect-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 48px;
  border: 2px solid #667eea;
  border-radius: 12px;
  background: transparent;
  color: #667eea;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.connect-btn:hover {
  background: #667eea;
  color: white;
}

.connecting-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 12px;
  color: #667eea;
  font-size: 14px;
  font-weight: 500;
}

/* Error Banner */
.error-banner {
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #eb3349 0%, #f45c43 100%);
  color: white;
  padding: 12px 16px;
  font-size: 13px;
  font-weight: 500;
}

/* ===== Animations ===== */
@keyframes shake {
  0%,
  100% {
    transform: translateX(0);
  }
  20%,
  60% {
    transform: translateX(-5px);
  }
  40%,
  80% {
    transform: translateX(5px);
  }
}

@keyframes gradientShift {
  0% {
    filter: hue-rotate(0deg);
  }
  100% {
    filter: hue-rotate(20deg);
  }
}

@keyframes iconPulse {
  0%,
  100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
}

@keyframes ringExpand {
  0% {
    transform: translate(-50%, -50%) scale(0.8);
    opacity: 1;
  }
  100% {
    transform: translate(-50%, -50%) scale(1.5);
    opacity: 0;
  }
}

@keyframes blink {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.4;
  }
}

@keyframes avatarRing {
  0% {
    transform: scale(1);
    opacity: 0.6;
  }
  100% {
    transform: scale(1.8);
    opacity: 0;
  }
}

@keyframes badgePulse {
  0%,
  100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.02);
  }
}

@keyframes dialBtnAppear {
  from {
    opacity: 0;
    transform: scale(0.8);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

@keyframes endBtnPulse {
  0%,
  100% {
    box-shadow: 0 4px 15px rgba(235, 51, 73, 0.4);
  }
  50% {
    box-shadow: 0 4px 25px rgba(235, 51, 73, 0.6);
  }
}

/* Vue Transitions */
.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: all 0.3s ease;
}
.slide-fade-enter,
.slide-fade-leave-to {
  transform: translateY(-10px);
  opacity: 0;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.fade-up-enter-active,
.fade-up-leave-active {
  transition: all 0.3s ease;
}
.fade-up-enter,
.fade-up-leave-to {
  transform: translateY(10px);
  opacity: 0;
}

.scale-enter-active,
.scale-leave-active {
  transition: all 0.3s ease;
}
.scale-enter,
.scale-leave-to {
  transform: scale(0.9);
  opacity: 0;
}

.slide-up-enter-active,
.slide-up-leave-active {
  transition: all 0.3s ease;
}
.slide-up-enter,
.slide-up-leave-to {
  transform: translateY(100%);
  opacity: 0;
}

.number-pop-enter-active,
.number-pop-leave-active {
  transition: all 0.2s ease;
}
.number-pop-enter,
.number-pop-leave-to {
  transform: scale(1.1);
  opacity: 0;
}

/* ===== Responsive ===== */
@media (max-width: 400px) {
  .compact-dialer {
    border-radius: 32px !important;
  }

  .dial-btn {
    height: 56px;
  }

  .digit-number {
    font-size: 20px;
  }

  .call-number-display {
    font-size: 20px;
  }
}

/* ===== Dark Mode Support ===== */
/* Vuetify theme class based dark mode */
.theme--dark .dialer-body,
.theme--dark.compact-dialer .dialer-body {
  background: #1a1a2e;
}

.theme--dark .phone-input-container,
.theme--dark.compact-dialer .phone-input-container {
  background: #252542;
  border-color: #3d3d6b;
}

.theme--dark .phone-input-container.input-focused,
.theme--dark.compact-dialer .phone-input-container.input-focused {
  background: #2d2d50;
  border-color: #667eea;
}

.theme--dark .phone-input,
.theme--dark.compact-dialer .phone-input {
  color: #ffffff !important;
}

.theme--dark .phone-input::placeholder,
.theme--dark.compact-dialer .phone-input::placeholder {
  color: #9e9e9e !important;
}

.theme--dark .dial-pad,
.theme--dark.compact-dialer .dial-pad {
  background: #252542;
}

.theme--dark .dial-btn,
.theme--dark.compact-dialer .dial-btn {
  background: #2d2d50;
}

.theme--dark .digit-number,
.theme--dark.compact-dialer .digit-number {
  color: #ffffff;
}

.theme--dark .digit-letters,
.theme--dark.compact-dialer .digit-letters {
  color: #9e9e9e;
}

.theme--dark .call-status-section,
.theme--dark.compact-dialer .call-status-section {
  background: linear-gradient(135deg, #252542 0%, #1a1a2e 100%);
}

.theme--dark .call-number-display,
.theme--dark.compact-dialer .call-number-display {
  color: #ffffff;
}

.theme--dark .call-name-display,
.theme--dark.compact-dialer .call-name-display {
  color: #ffffff;
}

.theme--dark .contact-results-dropdown,
.theme--dark.compact-dialer .contact-results-dropdown {
  background: #252542;
  border-color: #3d3d6b;
}

.theme--dark .contact-result-item,
.theme--dark.compact-dialer .contact-result-item {
  border-bottom-color: #3d3d6b;
}

.theme--dark .contact-result-item:hover,
.theme--dark.compact-dialer .contact-result-item:hover {
  background: rgba(102, 126, 234, 0.15);
}

.theme--dark .contact-name,
.theme--dark.compact-dialer .contact-name {
  color: #ffffff;
}

.theme--dark .contact-phone,
.theme--dark.compact-dialer .contact-phone {
  color: #b0b0b0;
}

.theme--dark .contact-list-badge,
.theme--dark.compact-dialer .contact-list-badge {
  background: linear-gradient(
    135deg,
    rgba(102, 126, 234, 0.25) 0%,
    rgba(118, 75, 162, 0.25) 100%
  );
  color: #a1b4f8;
}

.theme--dark .contact-list-badge .v-icon,
.theme--dark.compact-dialer .contact-list-badge .v-icon {
  color: #a1b4f8 !important;
}

.theme--dark .contact-results-hint,
.theme--dark.compact-dialer .contact-results-hint {
  background: #1a1a2e;
  color: #9e9e9e;
}

.theme--dark .compact-dialer,
.theme--dark.compact-dialer {
  background: #1a1a2e;
}

.theme--dark .input-icon .v-icon,
.theme--dark.compact-dialer .input-icon .v-icon {
  color: #9e9e9e !important;
}

.theme--dark .clear-btn .v-icon,
.theme--dark.compact-dialer .clear-btn .v-icon {
  color: #9e9e9e !important;
}

.theme--dark .dialpad-toggle .v-icon,
.theme--dark.compact-dialer .dialpad-toggle .v-icon {
  color: #b0b0b0 !important;
}

.theme--dark .dialpad-toggle.active .v-icon,
.theme--dark.compact-dialer .dialpad-toggle.active .v-icon {
  color: white !important;
}

.theme--dark .backspace-btn .v-icon,
.theme--dark.compact-dialer .backspace-btn .v-icon {
  color: #9e9e9e !important;
}

/* System preference based dark mode (fallback) */
@media (prefers-color-scheme: dark) {
  .dialer-body {
    background: #1a1a2e;
  }

  .phone-input-container {
    background: #252542;
    border-color: #3d3d6b;
  }

  .phone-input-container.input-focused {
    background: #2d2d50;
    border-color: #667eea;
  }

  .phone-input {
    color: #ffffff !important;
  }

  .phone-input::placeholder {
    color: #9e9e9e !important;
  }

  .dial-pad {
    background: #252542;
  }

  .dial-btn {
    background: #2d2d50;
  }

  .digit-number {
    color: #ffffff;
  }

  .digit-letters {
    color: #9e9e9e;
  }

  .call-status-section {
    background: linear-gradient(135deg, #252542 0%, #1a1a2e 100%);
  }

  .call-number-display {
    color: #ffffff;
  }

  .call-name-display {
    color: #ffffff;
  }

  .contact-results-dropdown {
    background: #252542;
    border-color: #3d3d6b;
  }

  .contact-result-item {
    border-bottom-color: #3d3d6b;
  }

  .contact-result-item:hover {
    background: rgba(102, 126, 234, 0.15);
  }

  .contact-name {
    color: #ffffff;
  }

  .contact-phone {
    color: #b0b0b0;
  }

  .contact-list-badge {
    background: linear-gradient(
      135deg,
      rgba(102, 126, 234, 0.25) 0%,
      rgba(118, 75, 162, 0.25) 100%
    );
    color: #a1b4f8;
  }

  .contact-list-badge .v-icon {
    color: #a1b4f8 !important;
  }

  .contact-results-hint {
    background: #1a1a2e;
    color: #9e9e9e;
  }

  .compact-dialer {
    background: #1a1a2e;
  }
}
</style>

<!-- Global styles for dialog wrapper (not scoped) -->
<style>
.v-dialog.dialer-dialog {
  border-radius: 40px !important;
  overflow: visible !important; /* Allow dropdown to overflow */
  z-index: 200 !important; /* Standard dialog z-index */
}

.v-dialog.dialer-dialog > .v-card,
.v-dialog.dialer-dialog .v-card.compact-dialer {
  border-radius: 40px !important;
  overflow: visible !important; /* Allow dropdown to overflow */
}

.v-dialog.dialer-dialog .v-card__text.pb-2,
.v-dialog.dialer-dialog .dialer-body.pb-2 {
  padding-bottom: 22px !important;
  overflow: visible !important; /* Don't clip dropdown */
}

/* Fallback with higher specificity */
.v-application .v-dialog.dialer-dialog {
  border-radius: 40px !important;
  overflow: visible !important;
}

.v-application .v-dialog.dialer-dialog .v-card {
  border-radius: 40px !important;
  overflow: visible !important;
}

/* Ensure dropdown appears above everything */
.v-application .contact-results-dropdown {
  max-height: 150px !important;
  overflow-y: auto !important;
  z-index: 20000 !important;
}

.v-application .phone-input-wrapper {
  overflow: visible !important;
  z-index: 10000 !important;
}

/* ===== Dark Theme Support (Vuetify) ===== */
.theme--dark .compact-dialer {
  background: #1a1a2e !important;
}

.theme--dark .compact-dialer .dialer-body {
  background: #1a1a2e !important;
}

.theme--dark .compact-dialer .phone-input-container {
  background: #252542 !important;
  border-color: #3d3d6b !important;
}

.theme--dark .compact-dialer .phone-input-container.input-focused {
  background: #2d2d50 !important;
  border-color: #667eea !important;
}

.theme--dark .compact-dialer .phone-input {
  color: #ffffff !important;
}

.theme--dark .compact-dialer .phone-input::placeholder {
  color: #9e9e9e !important;
}

.theme--dark .compact-dialer .dial-pad {
  background: #252542 !important;
}

.theme--dark .compact-dialer .dial-btn {
  background: #2d2d50 !important;
}

.theme--dark .compact-dialer .digit-number {
  color: #ffffff !important;
}

.theme--dark .compact-dialer .digit-letters {
  color: #9e9e9e !important;
}

.theme--dark .compact-dialer .call-status-section {
  background: linear-gradient(135deg, #252542 0%, #1a1a2e 100%) !important;
}

.theme--dark .compact-dialer .call-number-display {
  color: #ffffff !important;
}

.theme--dark .compact-dialer .call-name-display {
  color: #ffffff !important;
}

.theme--dark .compact-dialer .contact-results-dropdown {
  background: #252542 !important;
  border-color: #3d3d6b !important;
}

.theme--dark .compact-dialer .contact-result-item {
  border-bottom-color: #3d3d6b !important;
}

.theme--dark .compact-dialer .contact-result-item:hover {
  background: rgba(102, 126, 234, 0.15) !important;
}

.theme--dark .compact-dialer .contact-name {
  color: #ffffff !important;
}

.theme--dark .compact-dialer .contact-phone {
  color: #b0b0b0 !important;
}

.theme--dark .compact-dialer .contact-list-badge {
  background: linear-gradient(
    135deg,
    rgba(102, 126, 234, 0.25) 0%,
    rgba(118, 75, 162, 0.25) 100%
  ) !important;
  color: #a1b4f8 !important;
}

.theme--dark .compact-dialer .contact-list-badge .v-icon {
  color: #a1b4f8 !important;
}

.theme--dark .compact-dialer .contact-results-hint {
  background: #1a1a2e !important;
  color: #9e9e9e !important;
}

.theme--dark .compact-dialer .input-icon .v-icon {
  color: #9e9e9e !important;
}

.theme--dark .compact-dialer .clear-btn .v-icon {
  color: #9e9e9e !important;
}

.theme--dark .compact-dialer .dialpad-toggle .v-icon {
  color: #b0b0b0 !important;
}

.theme--dark .compact-dialer .dialpad-toggle.active .v-icon {
  color: white !important;
}

.theme--dark .compact-dialer .backspace-btn .v-icon {
  color: #9e9e9e !important;
}
</style>
